import { constant } from 'lodash';
import { type CSSProperties, type ReactNode, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';
import { type Upload, UploadStatus } from '@air/redux-uploader';
import { ClipUtils } from '@air/utils-clips';

import { GalleryItemType } from '~/components/Gallery/types';
import { UploadTableRow } from '~/components/TableView/PrivateTableView/UploadTableRow';
import { getLoaderRow, MOBILE_TABLE_ROW_HEIGHT, TABLE_ROW_HEIGHT } from '~/components/TableView/tableRowUtils';
import { type AirTableRow } from '~/components/TableView/types';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { useBoardsBeingDeleted } from '~/swr-hooks/boards/useBoardsBeingDeleted';
import { type UseGalleryMixedData } from '~/swr-hooks/gallery/types';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';

export type UsePrivateGetTableRowsParams = {
  loadMore: () => void;
  uploads: Upload[];
  renderBoardRow?: (params: { rowStyle: CSSProperties; key: string | number; data: Board }) => ReactNode;
  isBoardSelectable?: (boardId: string) => boolean;
  renderAssetRow?: (params: { rowStyle: CSSProperties; key: string | number; data: Clip }) => ReactNode;
  isAssetSelectable?: (clipId: string) => boolean;
} & Pick<UseGalleryMixedData<Board, Clip>, 'data' | 'hasMore' | 'isLoading' | 'isLoadingMore'>;

export const usePrivateTableRows = ({
  data,
  hasMore,
  isLoading,
  isLoadingMore,
  loadMore,
  uploads,
  renderAssetRow,
  isAssetSelectable = constant(false),
  renderBoardRow,
  isBoardSelectable = constant(false),
}: UsePrivateGetTableRowsParams) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
  const boardsBeingDeleted = useBoardsBeingDeleted();

  const filteredUploads = useMemo(() => {
    const finishedClipIds = new Set(
      data
        .filter((row): row is { type: 'asset' | 'file'; item: Clip } => row.type === 'asset' || row.type === 'file')
        .filter((row) => ClipUtils.isTranscodeFinished(row.item.status))
        .map((row) => row.item.id),
    );

    return uploads.filter((u) => {
      if (finishedClipIds.has(u.clipId)) {
        return false;
      }
      // Upload is done but clip is gone (deleted) — don't show zombie upload row
      if (u.status === UploadStatus.completed) {
        return false;
      }

      return true;
    });
  }, [data, uploads]);

  const activeClipIds = useMemo(() => new Set(filteredUploads.map((u) => u.clipId)), [filteredUploads]);

  const rows = useMemo(() => {
    const tableRows: AirTableRow[] = [];

    filteredUploads.forEach((upload) => {
      tableRows.push({
        height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
        getMetadata: () => ({
          isSelectable: false,
          type: GalleryItemType.upload,
          item: upload,
        }),
        render: (rowStyle, key) => (
          <UploadTableRow
            key={key}
            upload={upload}
            style={{
              ...rowStyle,
              paddingLeft: responsiveHorizontalPadding,
              paddingRight: responsiveHorizontalPadding,
            }}
          />
        ),
      });
    });

    data.forEach((rowData) => {
      switch (rowData.type) {
        case 'board':
          {
            if (!boardsBeingDeleted.includes(rowData.item.id)) {
              tableRows.push({
                height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
                getMetadata: () => ({
                  isSelectable: isBoardSelectable(rowData.item.id),
                  item: rowData.item,
                  type: GalleryItemType.board,
                }),
                render: (rowStyle, key) => renderBoardRow?.({ rowStyle, key, data: rowData.item }),
              });
            }
          }
          break;

        case 'asset':
        case 'file':
          if (!activeClipIds.has(rowData.item.id)) {
            tableRows.push({
              height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
              getMetadata: () => ({
                isSelectable: isAssetSelectable(rowData.item.id),
                item: rowData.item,
                type: getAssetGalleryItemType(rowData.item),
              }),
              render: (rowStyle, key) => renderAssetRow?.({ rowStyle, key, data: rowData.item }),
            });
          }
          break;
      }
    });

    if (hasMore || isLoading || isLoadingMore) {
      tableRows.push(getLoaderRow(isLoading, loadMore));
    }

    return tableRows;
  }, [
    filteredUploads,
    activeClipIds,
    data,
    hasMore,
    isLoading,
    isLoadingMore,
    isAboveMediumScreen,
    responsiveHorizontalPadding,
    boardsBeingDeleted,
    isBoardSelectable,
    renderBoardRow,
    isAssetSelectable,
    renderAssetRow,
    loadMore,
  ]);

  return rows;
};
